import axios from 'axios';
import { API_BASE_URL, ENDPOINTS } from '../constants/api';

// Configure axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});

// Applications
export const getApplications = async () => {
  try {
    // Use real API endpoint
    const response = await api.get(ENDPOINTS.APPLICATIONS.LIST);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch applications');
  }
};

export const createClientApplication = async (data) => {
  try {
    const response = await api.post(ENDPOINTS.APPLICATIONS.CLIENT_APP, data);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.response?.status === 409) {
      throw new Error('Subdomain already exists');
    } else {
      throw new Error('Failed to create demo account');
    }
  }
};

// Clients
export const getClients = async () => {
  try {
    // Use real API endpoint
    const response = await api.get(ENDPOINTS.CLIENTS.LIST);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch clients');
  }
};

export const saveClient = async (clientData) => {
  try {
    // Use real API endpoint
    const response = await api.post(ENDPOINTS.CLIENTS.CREATE, clientData);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to create client');
  }
};

export const updateClient = async (clientId, clientData) => {
  try {
    // Use real API endpoint
    const response = await api.put(ENDPOINTS.CLIENTS.UPDATE(clientId), clientData);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to update client');
  }
};

export const deleteClient = async (clientId) => {
  try {
    // Use real API endpoint
    const response = await api.delete(ENDPOINTS.CLIENTS.DELETE(clientId));
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete client');
  }
};

// Backend build and task execution
export async function buildBackend(clientInfo) {
  try {
    const response = await api.post('/track-and-trace/backend', clientInfo);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to build backend');
  }
}


export async function executeTask(taskId, clientInfo) {
  try {
    // Special handling for buildBackend task
    if (taskId === 'buildBackend') {
      return buildBackend(clientInfo);
    }
    
    // Default handling for other tasks
    const response = await api.post(ENDPOINTS.TASKS.EXECUTE(taskId), clientInfo);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to execute task');
  }
}

export const getDeployments = async () => {
  try {
    const response = await api.get(ENDPOINTS.DEPLOYMENTS.LIST);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch deployments');
  }
};

export const createDeployment = async (data) => {
  try {
    const response = await api.post(ENDPOINTS.DEPLOYMENTS.CREATE, data);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.messages?.message || 'Failed to create deployment');
  }
};

export const triggerDeployment = async (deploymentId, type) => {
  try {
      const response = await api.post(`${ENDPOINTS.DEPLOYMENTS.TRIGGER(deploymentId)}/${type}`);
      return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || `Failed to trigger ${type} deployment`);
  }
};

export const downloadArtifacts = async (deploymentId, type) => {
  try {
    // Use axios with responseType blob for file download
    const response = await api.get(`${ENDPOINTS.DEPLOYMENTS.DOWNLOAD_ARTIFACT(deploymentId)}`, {
      responseType: 'blob' // Important: This tells axios to handle the response as binary data
    });
    
    // Get the content type from the response
    const contentType = response.headers['content-type'];
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'artifact.apk'; // Default filename
    
    if (contentDisposition) {
      // Parse the filename from the header
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    // Create a blob URL from the response data
    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary link element and trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    link.remove();
    
    return { success: true };
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || `Failed to download artifact`);
  }
};

export const getDeployment = async (deploymentId) => {
  try {
    const response = await api.get(ENDPOINTS.DEPLOYMENTS.GET(deploymentId));
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch deployment');
  }
};

export const deleteDeployment = async (deploymentId) => {
  try {
    const response = await api.delete(ENDPOINTS.DEPLOYMENTS.DELETE(deploymentId));
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete deployment');
  }
};

export const reactivateDeployment = async (deploymentId) => {
  try {
    const response = await api.patch(`/deployments/${deploymentId}/reactivate`);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to reactivate deployment');
  }
}; 

export const deactivateDeployment = async (deploymentId) => {
  try {
    const response = await api.patch(`/deployments/${deploymentId}/deactivate`);
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw new Error(error.response?.data?.message || 'Failed to deactivate deployment');
  }
}; 

